import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe } from 'lucide-react';
import { useScheduler } from '@/contexts/SchedulerContext';
import { TIMEZONES } from '@/lib/timezones';

interface CurrentTimezoneSelectorProps {
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
}

export const CurrentTimezoneSelector: React.FC<CurrentTimezoneSelectorProps> = ({
  selectedTimezone,
  onTimezoneChange,
}) => {
  const { settings } = useScheduler();

  // Check if timezone feature is enabled
  const isTimezoneEnabled = settings.enableTimezones ?? false;

  // Get available timezones from admin settings
  // Only show timezones that admin has configured in Settings > Administrator Settings > Available Timezones
  const availableTimezones = useMemo(() => {
    const configured = settings.availableTimezones || [];
    return TIMEZONES.filter(tz => configured.includes(tz.value));
  }, [settings.availableTimezones]);

  // Don't render if timezone feature is not enabled
  if (!isTimezoneEnabled) {
    return null;
  }

  // Don't render if only one timezone is available
  if (availableTimezones.length <= 1) {
    return null;
  }

  const selectedEntry = TIMEZONES.find(tz => tz.value === selectedTimezone);

  return (
    <div className="flex items-center gap-2">
      <Globe className="h-4 w-4 text-muted-foreground" />
      <Select value={selectedTimezone} onValueChange={onTimezoneChange}>
        <SelectTrigger className="w-[200px] h-8">
          <SelectValue>
            {selectedEntry ? selectedEntry.label : selectedTimezone}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[240px]">
          {availableTimezones.map((tz) => (
            <SelectItem key={tz.value} value={tz.value}>
              {tz.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
